var AnimationLoop   = require("enyo-anim/AnimationLoop"),
    DrawLoop        = require("enyo-ui/DrawLoop"),
    PerformanceMode = require("enyo-perf/PerformanceMode"),
    kind            = require("enyo/kind"),
    utils           = require("enyo/utils");

require("enyo-ui");

/**
 * [RepeaterDrawLoopSupport]{@link enyo-ui.RepeaterDrawLoopSupport} extends
 * enyo-ui.Repeater and enyo-ui.SimpleRepeater to make use of enyo-ui.DrawLoop
 * for asynchronous drawing of repeater children.
 *
 * @mixin enyo-ui.RepeaterDrawLoopSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterDrawLoopSupport",

    /**
     * How many frames are allowed to exceed maxFrameDuration before we start
     * deferring drawing tasks.
     *
     * @name     maxDroppedFrames
     * @type     {int}
     * @default  5
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @public
     */

    /**
     * Frame duration threshold. Frames that take longer than this are
     * considered `dropped` frames.
     *
     * @name     maxFrameDuration
     * @type     {int}
     * @default  1000 / 60
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @public
     */

    /**
     * Counts dropped frames during time spent in performanceMode
     *
     * @type     {number}
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @private
     */
    droppedFrames: 0,

    /**
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.euid = this.euid || utils.uid();
            sup.apply(this, arguments);
            PerformanceMode.observe("on", this.bindSafely(this.performanceModeObserver));
        };
    }),


    /**
     * Evaluate settings.
     *
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            if (this.maxDroppedFrames === undefined) {
                this.maxDroppedFrames = 5;
            }

            if (this.maxFrameDuration === undefined) {
                this.maxFrameDuration = (1000 / 60);
            }
        };
    }),

    /**
     * React to changes in performanceMode.
     *
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @private
     */
    performanceModeObserver: function() {
        if (!PerformanceMode.on) {
            this.droppedFrames = 0;
            this.flushPerformanceModeQueue();
        }
    },

    /**
     * Update the dropped frame counter based on the duration of the last
     * animationFrame.
     *
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @protected
     */
    updateDroppedFrameCount: function() {
        if (AnimationLoop.lastFrameDuration > this.maxFrameDuration) {
            this.droppedFrames += Math.ceil(AnimationLoop.lastFrameDuration / this.maxFrameDuration) - 1;
        }
    },

    /**
     * Defer drawing in PerformanceMode if framerate drops too low.
     *
     * @returns  {bool} Whether to defer executing the next draw call.
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @protected
     */
    shouldDeferDrawInPerformanceMode: function() {
        this.updateDroppedFrameCount();

        if (this.droppedFrames > this.maxDroppedFrames) {
            return true;
        }
        else {
            return false;
        }
    },

    /**
     * Schedules drawNext call to be executed asynchronously.
     *
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @private
     */
    requestAsyncDrawNext: function() {
        DrawLoop.request(this.euid + "::drawNext", this.drawNext);
    },

    /**
     * Cancels the scheduled drawNext call.
     *
     * @memberof enyo-ui.RepeaterDrawLoopSupport
     * @private
     */
    cancelAsyncDrawNext: function() {
        DrawLoop.cancel(this.euid + "::drawNext");
    }

};
